/* <series-configurator> — within-edition config-card list.
 * Progressive enhancement over the Liquid-rendered card list: cards stay real
 * <a href> links (no-JS fallback = full-page nav). This element reorders the
 * cards by numeric data-sort ascending; ALL of the edition's configs are listed
 * directly (2026-07-04 Bill design revision — the former channel filter buttons
 * and "Add more cameras" upgrade rows were removed; see git history).
 * Compatible series can opt into the within-edition AJAX swap below.
 */
(function () {
  if (!('customElements' in window) || customElements.get('series-configurator')) return;

  class SeriesConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.cardsWrap = this.querySelector('.sc-cards');
      this.cards = Array.prototype.slice.call(this.querySelectorAll('.sc-card'));
      if (!this.cardsWrap || !this.cards.length) return;

      this.sortCards();
    }

    sortCards() {
      this.cards.sort(function (a, b) {
        return (parseInt(a.dataset.sort, 10) || 999) - (parseInt(b.dataset.sort, 10) || 999);
      });
      var wrap = this.cardsWrap;
      this.cards.forEach(function (c) { wrap.appendChild(c); }); // re-append in sorted order
    }
  }

  customElements.define('series-configurator', SeriesConfigurator);

  // ===================================================================
  // Phase 2B — opt-in within-edition AJAX narrow swap.
  // Document-scoped series keep native navigation so every product-owned section,
  // integration, and head field is refreshed as one server-rendered document.
  // ===================================================================
  var SWAP_IDS = ['#config-content-main', '#config-content-spec', '.product-detail-faq'];
  var prefetched = {}; // url -> Promise<Response> (mouseover prefetch cache)

  function allowsAjaxNavigation(card) {
    var configurator = card.closest('series-configurator');
    return !!configurator && configurator.getAttribute('data-navigation-mode') === 'ajax';
  }

  function isPlainPrimaryClick(event, card) {
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (card.hasAttribute('download')) return false;
    var target = card.getAttribute('target');
    return !target || target === '_self';
  }

  function fetchDoc(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'fetch' }, credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  function jsonLdHasType(value, type) {
    if (!value || typeof value !== 'object') return false;
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) if (jsonLdHasType(value[i], type)) return true;
      return false;
    }
    var nodeType = value['@type'];
    if (nodeType === type || (Array.isArray(nodeType) && nodeType.indexOf(type) !== -1)) return true;
    if (Array.isArray(value['@graph']) && jsonLdHasType(value['@graph'], type)) return true;
    return false;
  }

  function productJsonLd(root) {
    var scripts = root.querySelectorAll('script[type="application/ld+json"]');
    var result = [];
    var hasProduct = false;
    var hasBreadcrumb = false;
    for (var i = 0; i < scripts.length; i++) {
      try {
        var data = JSON.parse(scripts[i].textContent);
        var isProduct = jsonLdHasType(data, 'Product');
        var isBreadcrumb = jsonLdHasType(data, 'BreadcrumbList');
        if (isProduct || isBreadcrumb) result.push(scripts[i]);
        hasProduct = hasProduct || isProduct;
        hasBreadcrumb = hasBreadcrumb || isBreadcrumb;
      } catch (e) { /* unrelated invalid JSON-LD is left untouched */ }
    }
    if (!hasProduct || !hasBreadcrumb) throw new Error('missing product JSON-LD');
    return result;
  }

  // Build and validate the complete product-specific head set before changing
  // either the body or the live head. A missing required field triggers the
  // existing hard-navigation fallback without leaving a partially swapped page.
  function buildProductHeadPlan(root) {
    var title = root.querySelector('title');
    var description = root.querySelector('meta[name="description"]');
    var canonical = root.querySelector('link[rel="canonical"]');
    var social = root.querySelectorAll('meta[property^="og:"],meta[property^="product:"],meta[name^="twitter:"]');
    if (!title || !description || !canonical || !social.length) throw new Error('missing product head metadata');
    return {
      title: title,
      description: description,
      canonical: canonical,
      robots: root.querySelector('meta[name="robots"]'),
      alternates: root.querySelectorAll('link[rel="alternate"][hreflang]'),
      social: social,
      jsonLd: productJsonLd(root)
    };
  }

  function replaceHeadCollection(currentNodes, incomingNodes) {
    var marker = document.createComment('series-configurator-head-swap');
    var parent = currentNodes.length ? currentNodes[0].parentNode : document.head;
    parent.insertBefore(marker, currentNodes.length ? currentNodes[0] : null);
    for (var i = 0; i < incomingNodes.length; i++) {
      parent.insertBefore(document.importNode(incomingNodes[i], true), marker);
    }
    for (var j = 0; j < currentNodes.length; j++) currentNodes[j].remove();
    marker.remove();
  }

  function applyProductHeadPlan(current, incoming) {
    document.title = incoming.title.textContent;
    current.description.replaceWith(document.importNode(incoming.description, true));
    current.canonical.replaceWith(document.importNode(incoming.canonical, true));

    if (incoming.robots && current.robots) current.robots.replaceWith(document.importNode(incoming.robots, true));
    else if (incoming.robots && !current.robots) document.head.appendChild(document.importNode(incoming.robots, true));
    else if (!incoming.robots && current.robots) current.robots.remove();

    replaceHeadCollection(Array.prototype.slice.call(current.alternates), Array.prototype.slice.call(incoming.alternates));
    replaceHeadCollection(Array.prototype.slice.call(current.social), Array.prototype.slice.call(incoming.social));
    replaceHeadCollection(current.jsonLd, incoming.jsonLd);
  }

  // Sync the mobile sticky product-tab nav text + the H1 title from the swapped-in DOM.
  function syncTabNav(doc) {
    try {
      var newTitle = doc.querySelector('.product__info .product-meta__title');
      var curTitle = document.querySelector('.product__info .product-meta__title');
      // (the H1/H2 lives inside #config-content-main, so it is already swapped — this is a
      //  safety no-op there; the sticky tab form title below is OUTSIDE the swap region.)
      var newTabTitle = doc.querySelector('.product-tab-form > span.ellipsis-2');
      var curTabTitle = document.querySelector('.product-tab-form > span.ellipsis-2');
      if (newTabTitle && curTabTitle) curTabTitle.textContent = newTabTitle.textContent;
      // guard unused-var lint
      void newTitle; void curTitle;
    } catch (e) { /* non-fatal */ }
  }

  function reinitProduct() {
    if (typeof window.initProductBuyArea === 'function') window.initProductBuyArea();
    if (typeof window.initProductInfo === 'function') window.initProductInfo();
    if (typeof window.vfpReinit === 'function') window.vfpReinit();
    if (typeof window.initProductDetailFaq === 'function') window.initProductDetailFaq();
    if (typeof window.updateStoryGates === 'function') window.updateStoryGates(); // story sections: re-gate + sync [data-story-model] for the swapped-in config
    if (typeof window.updateT340StoryGates === 'function') window.updateT340StoryGates(); // T340-only fail-closed 3CH/4CH + camera-composition gates
  }

  var swapping = false;
  function swapTo(url, push) {
    if (push === undefined) push = true;
    if (swapping) return;
    swapping = true;
    var docPromise = prefetched[url]
      ? prefetched[url].then(function (res) { return res.clone().text(); }).then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); }).catch(function () { return fetchDoc(url); })
      : fetchDoc(url);

    docPromise.then(function (doc) {
      // Resolve BOTH swap targets (current in-page + incoming HTML) before mutating the DOM.
      // If anything is missing, hard-fallback to a full navigation rather than a half-swapped page.
      var pairs = [];
      for (var i = 0; i < SWAP_IDS.length; i++) {
        var cur = document.querySelector(SWAP_IDS[i]);
        var inc = doc.querySelector(SWAP_IDS[i]);
        if (!cur || !inc) { throw new Error('missing swap target ' + SWAP_IDS[i]); }
        pairs.push({ selector: SWAP_IDS[i], html: inc.outerHTML });
      }
      var currentHeadPlan = buildProductHeadPlan(document);
      var headPlan = buildProductHeadPlan(doc);
      // Replace all AFTER both resolved. We assign outerHTML (NOT importNode/replaceWith) so the
      // browser RE-PARSES the markup and upgrades the theme's custom elements (<product-variants>,
      // <product-form>, price-list, etc.) from a clean slate. importNode produced element instances
      // whose custom-element lifecycle did not re-wire correctly against theme.js — specifically the
      // variant picker's masterSelector ended up non-functional, breaking variant selection (the
      // hidden variant id / add-to-cart) on the swapped-in config. outerHTML fixes that. (Re-query by
      // selector after each assignment because outerHTML invalidates the old element reference.)
      pairs.forEach(function (pr) {
        var cur = document.querySelector(pr.selector);
        if (cur) cur.outerHTML = pr.html;
      });
      applyProductHeadPlan(currentHeadPlan, headPlan);
      if (push) history.pushState({}, '', url);
      syncTabNav(doc);
      window.scrollTo({ top: 0 });
      reinitProduct();
    }).catch(function (err) {
      // Hard fallback: never leave a half-swapped page.
      void err;
      window.location.href = url;
    }).then(function () {
      swapping = false;
    });
  }

  // Register delegated handlers ONCE for the whole document (survive swaps).
  if (!window.__scSwapBound) {
    window.__scSwapBound = true;

    document.addEventListener('click', function (e) {
      var card = e.target && e.target.closest ? e.target.closest('series-configurator .sc-card') : null;
      if (!card) return;
      // coming_soon (aria-disabled) or no href -> not swappable; let it be (no nav).
      if (card.hasAttribute('aria-disabled')) { e.preventDefault(); return; }
      var href = card.getAttribute('href');
      if (!href) return;
      if (!isPlainPrimaryClick(e, card)) return;
      // already the current config -> nothing to do.
      if (card.classList.contains('is-current')) { e.preventDefault(); return; }
      if (!allowsAjaxNavigation(card)) return;
      var configurator = card.closest('series-configurator');
      var currentStoryProfile = configurator ? configurator.getAttribute('data-story-profile') : '';
      var targetStoryProfile = card.getAttribute('data-story-profile');
      if (currentStoryProfile && targetStoryProfile && currentStoryProfile !== targetStoryProfile) return;
      e.preventDefault();
      swapTo(card.href);
    });

    document.addEventListener('mouseover', function (e) {
      var card = e.target && e.target.closest ? e.target.closest('series-configurator .sc-card') : null;
      if (!card || card.hasAttribute('aria-disabled') || card.classList.contains('is-current')) return;
      var href = card.getAttribute('href');
      if (!href || prefetched[card.href]) return;
      if (!allowsAjaxNavigation(card)) return;
      var configurator = card.closest('series-configurator');
      var currentStoryProfile = configurator ? configurator.getAttribute('data-story-profile') : '';
      var targetStoryProfile = card.getAttribute('data-story-profile');
      if (currentStoryProfile && targetStoryProfile && currentStoryProfile !== targetStoryProfile) return;
      prefetched[card.href] = fetch(card.href, { headers: { 'X-Requested-With': 'fetch' }, credentials: 'same-origin' }).catch(function () { return null; });
    });

    window.addEventListener('popstate', function () {
      if (!document.querySelector('series-configurator[data-navigation-mode="ajax"]')) return;
      swapTo(location.href, false);
    });
  }
})();
